import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  TrendingUp, 
  BarChart3, 
  BookOpen, 
  Shield, 
  Users, 
  Award,
  ArrowRight,
  CheckCircle,
  Play,
  Star,
  UserPlus,
  LogIn,
  Sparkles,
  Zap,
  Target,
  Globe
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    clearError();
    
    const result = await login(data);
    
    if (result.success) {
      navigate('/dashboard');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 relative overflow-hidden">
      {/* Professional White & Blue Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Elegant Blue Gradient Orbs */}
        <div 
          className="absolute w-[600px] h-[600px] bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/10 rounded-full blur-3xl animate-pulse"
          style={{
            left: `${mousePosition.x / 40}px`,
            top: `${mousePosition.y / 40}px`,
            transform: 'translate(-50%, -50%)'
          }}
        />
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-r from-indigo-200/15 to-blue-100/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
        <div className="absolute bottom-1/3 right-1/3 w-[550px] h-[550px] bg-gradient-to-r from-slate-300/12 to-blue-200/8 rounded-full blur-3xl animate-pulse animation-delay-4000" />
        
        {/* Professional Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.03'%3E%3Ccircle cx='40' cy='40' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '80px 80px'
          }} />
        </div>

        {/* Elegant Light Rays */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-200/20 to-transparent" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-indigo-300/15 to-transparent" />
        <div className="absolute top-0 left-2/3 w-px h-full bg-gradient-to-b from-transparent via-slate-200/10 to-transparent" />
      </div>

      {/* Professional Navigation */}
      <nav className="relative z-10 px-6 py-4 flex justify-between items-center backdrop-blur-xl bg-white/80 border-b border-blue-100/50">
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group hover:scale-110 transition-all duration-300">
              <span className="text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300">ET</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">EducateTrade</h1>
              <p className="text-xs text-gray-600 font-medium">Professional LSE Trading Platform</p>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-8">
          <a href="#features" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Features</a>
          <a href="#about" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">About</a>
          <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Contact</a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-100px)] py-8">
          
                    {/* Left Side - Professional Hero Content */}
          <div className="space-y-8 fadeInUp">
            <div className="space-y-6">
              <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100/80 via-indigo-50/60 to-slate-100/80 backdrop-blur-xl border border-blue-200/50 text-gray-700 rounded-full text-sm font-semibold shadow-lg shadow-blue-200/30">
                <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
                Professional LSE Trading Education Platform
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 leading-tight tracking-tight">
                Master the
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 animate-gradient"> London Stock Exchange</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl font-light">
                Learn professional trading strategies, practice with real-time LSE data, and build your portfolio with our comprehensive educational platform designed for UK markets.
              </p>
            </div>

            {/* Professional Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-6 w-6 text-blue-800" />
                </div>
                <span className="text-sm font-bold text-gray-700">Real-time LSE Data</span>
              </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-indigo-100 to-slate-200 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-6 w-6 text-indigo-800" />
                </div>
                <span className="text-sm font-bold text-gray-700">Risk-Free Practice</span>
              </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-slate-100 to-blue-200 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-6 w-6 text-slate-700" />
                </div>
                <span className="text-sm font-bold text-gray-700">Fresh News</span>
              </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-6 w-6 text-blue-700" />
                </div>
                <span className="text-sm font-bold text-gray-700">Portfolio Tracking</span>
              </div>
            </div>
          </div>

                    {/* Right Side - Professional Login Form */}
          <div className="slideInRight">
            <div className="relative">
              {/* Professional Glass Morphism Card */}
              <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl border border-blue-100/50 p-8 hover:bg-white/90 transition-all duration-500 group">
                {/* Professional Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                
                <div className="text-center mb-8">
                  <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-2xl">ET</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-3 tracking-tight">
                    Welcome Back
                  </h2>
                  <p className="text-gray-600 text-lg font-light">
                    Sign in to continue your trading education
                  </p>
                </div>

                                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-5">
                    <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          className="w-full pl-12 pr-4 py-4 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-2xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                          })}
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          className="w-full pl-12 pr-12 py-4 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-2xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium"
                          {...register('password', {
                            required: 'Password is required',
                            minLength: {
                              value: 8,
                              message: 'Password must be at least 8 characters',
                            },
                          })}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-900 transition-colors duration-300"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.password.message}</p>
                      )}
                    </div>
                  </div>

                                  {error && (
                    <div className="rounded-2xl bg-red-100/80 backdrop-blur-xl p-4 border border-red-200/50">
                      <div className="flex items-center">
                        <div className="ml-3">
                          <h3 className="text-sm font-semibold text-red-700">
                            {error}
                          </h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 hover:from-blue-700 hover:via-indigo-700 hover:to-slate-800 text-white font-bold text-lg rounded-2xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none btn-premium"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        <span>Signing in...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <LogIn className="h-5 w-5 mr-2" />
                        <span>Sign in to EducateTrade</span>
                      </div>
                    )}
                  </button>

                  <div className="text-center mt-6 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 nav-link-entrance">
                    <p className="text-gray-700 text-lg font-medium">
                      New to EducateTrade?{' '}
                      <Link
                        to="/register"
                        className="inline-block font-bold text-blue-600 hover:text-blue-700 transition-all duration-300 hover:scale-105 hover:text-shadow-lg hover:drop-shadow-lg"
                        style={{
                          textShadow: '0 0 10px rgba(59, 130, 246, 0.5)',
                          filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))'
                        }}
                      >
                        Create your account
                      </Link>
                    </p>
                    <div className="mt-2 text-sm text-gray-600">
                      Welcome back to your trading platform
                    </div>
                  </div>
              </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Features Section */}
      <div id="features" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100/80 via-indigo-50/60 to-slate-100/80 backdrop-blur-xl border border-blue-200/50 text-gray-700 rounded-full text-sm font-semibold shadow-lg shadow-blue-200/30 mb-6">
              <Star className="h-4 w-4 mr-2 text-blue-600" />
              Why Choose EducateTrade?
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6 tracking-tight">
              Professional Trading
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 animate-gradient"> Education</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
              Comprehensive tools and resources to help you master LSE trading with confidence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="h-10 w-10 text-blue-800" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Real Market Data</h3>
              <p className="text-gray-600 leading-relaxed font-light">Access live LSE data and practice with real market conditions in a risk-free environment</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-indigo-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-200/30 group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="h-10 w-10 text-indigo-800" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Fresh Market News</h3>
              <p className="text-gray-600 leading-relaxed font-light">Stay updated with latest market news and insights during trading hours</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-slate-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-slate-200/30 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Risk-Free Learning</h3>
              <p className="text-gray-600 leading-relaxed font-light">Practice trading strategies without financial risk and build confidence</p>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Footer - Made More Visible */}
      <footer className="relative bg-gradient-to-r from-slate-800 via-blue-900 to-slate-800 text-white mt-20">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 via-blue-900/30 to-slate-900/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Company Info */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-3 mb-4">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <span className="text-white font-bold text-xl">ET</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold tracking-tight">EducateTrade</h3>
                  <p className="text-blue-200 text-sm font-medium">Professional LSE Trading Platform</p>
                </div>
              </div>
            </div>

            {/* Address - Made More Prominent */}
            <div className="text-center">
              <div className="inline-flex items-center px-8 py-6 bg-white/20 backdrop-blur-xl border-2 border-white/30 rounded-3xl shadow-2xl">
                <div className="text-center">
                  <h4 className="text-xl font-bold text-white mb-3">📍 Our Location</h4>
                  <p className="text-white font-bold text-lg leading-relaxed">
                    84 Shinfield Road<br />
                    Reading RG2 7DA<br />
                    England
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Border */}
          <div className="mt-12 pt-8 border-t border-white/30">
            <div className="text-center">
              <p className="text-blue-200 text-sm">
                © 2025 EducateTrade. Professional trading education for the London Stock Exchange.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login; 